import { Divider, Group, Stack } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { Trans } from '@lingui/react';
import { IconRefresh } from '@tabler/icons';
import { FC, Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import BookitupAddIcon from '../../../Atoms/BookitupAddIcon';
import BookitupThemeIcon from '../../../Atoms/BookitupThemeIcon';
import EmptyText from '../../../Atoms/EmptyText';
import SkeletonItems from '../../../Atoms/SkeletonItems';
import { CustomModalHeader } from '../../../Molecules/Dialogs';
import TaskListItem from '../../../Molecules/Event/Tasks/TaskListItem';
import PanePaper from '../../../Molecules/PanePaper';
import { useBookitupSubscription } from '../../../Service/Context/BookitupSubscriptionContext';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { TaskListContext } from '../../../Service/Context/TaskListContext';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../Service/LicenceS';
import { ToastS } from '../../../Service/ToastS';
import { fetchTasksByContactIds } from '../../../Service/restapi/taskService';
import { AgendaItem } from '../../../Types/AgendaItem';
import AgendaGroup from '../../../Types/AgendaGroup';
import AgendaGroupEntityType from '../../../Types/Enums/AgendaGroupEntityType';

type Props = {
  contactId: number;
};

const ContactTasks: FC<Props> = ({ contactId }) => {
  const [tasks, setTasks] = useState<AgendaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const { openContextModal, closeAll } = useModals();
  const { profile, bookitupTeam } = useContext(ProfileContext);
  const { teamAddonPresent } = useBookitupSubscription();
  const { executeWithLicenceCheck } = useContext(ViolationContext);

  const refresh = useCallback(() => setReloadTrigger((current) => current + 1), []);

  useEffect(() => {
    let active = true;

    setLoading(true);
    fetchTasksByContactIds([contactId])
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`could not load tasks for contact ${contactId}`);
        }

        const fetchedTasks: AgendaItem[] = await response.json();
        if (active) {
          setTasks(fetchedTasks);
        }
      })
      .catch(() => {
        if (active) {
          setTasks([]);
          ToastS.error('fetch.contact.tasks', 'Aufgaben konnten nicht geladen werden');
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [contactId, reloadTrigger]);

  const taskGroups = useMemo(
    () =>
      tasks.reduce<AgendaGroup[]>((groups, task) => {
        if (!task.groupId || groups.some((group) => group.id === task.groupId)) {
          return groups;
        }

        return [
          ...groups,
          {
            id: task.groupId,
            name: task.groupName,
            entityType: task.entityType as AgendaGroupEntityType,
            lastModified: task.lastModified,
          },
        ];
      }, []),
    [tasks],
  );

  const taskListContext = useMemo(
    () => ({
      taskListFilter: {
        selectedTaskGroups: [],
        selectedDateRange: [],
        includeCompleted: true,
        applicationUsersOfInterests: [],
        page: 0,
      },
      taskListContent: { agendaItems: tasks, taskGroups },
      totalPages: 1,
      changeFilter: () => undefined,
      refresh,
      loading,
      loadGroups: () => undefined,
    }),
    [loading, refresh, taskGroups, tasks],
  );

  const openTaskCreateModal = () =>
    openContextModal('taskCreateModal', {
      title: (
        <CustomModalHeader>
          <Trans id="task.create" />
        </CustomModalHeader>
      ),
      innerProps: {
        profile,
        bookitupTeam,
        teamAddonPresent,
        entityType: 'profiles',
        initialContactId: contactId,
        showEntityTypeSelection: true,
        finish: () => {
          closeAll();
          refresh();
        },
      },
      centered: true,
    });

  return (
    <PanePaper
      title="Aufgaben"
      rightSection={
        <Group spacing="xs">
          {!loading && (
            <BookitupThemeIcon onClick={refresh}>
              <IconRefresh />
            </BookitupThemeIcon>
          )}
          <BookitupAddIcon
            onClick={() => executeWithLicenceCheck(LicenceS.Restrictions.SAVE_TASK, openTaskCreateModal)}
          />
        </Group>
      }
    >
      {loading && <SkeletonItems height={50} />}
      {!loading && tasks.length === 0 && <EmptyText />}
      {!loading && tasks.length > 0 && (
        <TaskListContext.Provider value={taskListContext}>
          <Stack spacing={0}>
            {tasks.map((task, index) => (
              <Fragment key={task.id}>
                <TaskListItem task={task} />
                {index < tasks.length - 1 && <Divider />}
              </Fragment>
            ))}
          </Stack>
        </TaskListContext.Provider>
      )}
    </PanePaper>
  );
};

export default ContactTasks;
